var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useMutation } from '@tanstack/react-query';
import api from '../../api/index';
import useCustomerStore from '../../stores/customerStore';
import useWidgetStore from '../../stores/widgetStore';
import { FileIcon } from '../icons/FileIcon';
import { FileUploadIcon } from '../icons/FileUploadIcon';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { renderQuestionLabel } from '../../utils/common';
var MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
var ALLOWED_EXTENSIONS = [
    'pdf',
    'jpg',
    'jpeg',
    'png',
    'doc',
    'docx',
    'xls',
    'xlsx',
];
var getErrorMessage = function (error) {
    var _a, _b, _c;
    if (!error)
        return 'An unknown error occurred';
    if (error.message === 'Network Error')
        return 'Network Error: Please check your connection.';
    if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 413)
        return 'File size too large.';
    return ((_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) || error.message || 'Upload failed.';
};
var validateFiles = function (files) {
    var oversizedFiles = files.filter(function (_a) {
        var size = _a.size;
        return size > MAX_FILE_SIZE;
    });
    var invalidFiles = files.filter(function (file) {
        var _a;
        var extension = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        return !extension || !ALLOWED_EXTENSIONS.includes(extension);
    });
    if (oversizedFiles.length > 0) {
        return 'File exceeds the maximum size of 20MB';
    }
    else if (invalidFiles.length > 0) {
        return 'Only PDF, JPEG, PNG, Word, and Excel files are allowed.';
    }
    return undefined;
};
var FileUpload = function (_a) {
    var label = _a.label, question = _a.question, questionKey = _a.questionKey, value = _a.value, handleChange = _a.handleChange, _b = _a.error, error = _b === void 0 ? false : _b, _c = _a.allowMultiple, allowMultiple = _c === void 0 ? false : _c, placeholder = _a.placeholder, variant = _a.variant;
    var _d = useState(''), err = _d[0], setErr = _d[1];
    var _e = useState(false), confirmDeleteAll = _e[0], setConfirmDeleteAll = _e[1];
    var _f = useState(null), deletingFileUrl = _f[0], setDeletingFileUrl = _f[1];
    var containerRef = useRef(null);
    var fileInputRef = useRef(null);
    var extCustomerID = useCustomerStore().extCustomerID;
    var activeDraftQuote = useWidgetStore().activeDraftQuote;
    var uploadFilesUsingPresignedUrls = function (_a) {
        var files = _a.files, quoteId = _a.quoteId, externalID = _a.externalID;
        return __awaiter(void 0, void 0, void 0, function () {
            var data;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, api.quote.getPresignedSubmissionUrls({
                            files: files,
                            quoteId: quoteId,
                            externalID: externalID
                        })];
                    case 1:
                        data = (_b.sent()).data;
                        return [4 /*yield*/, Promise.all(data.files.map(function (item) {
                                var file = files.find(function (f) { return f.name === item.fileName; });
                                if (file) {
                                    return fetch(item.preSignedUrl, {
                                        method: 'PUT',
                                        headers: {
                                            'Content-Type': file.type
                                        },
                                        body: file
                                    });
                                }
                                else {
                                    console.log('File not found for upload:', item.fileName);
                                }
                            }))];
                    case 2:
                        _b.sent();
                        return [2 /*return*/, data.files.map(function (f) { return f.key; })];
                }
            });
        });
    };
    var uploadFileMutation = useMutation(uploadFilesUsingPresignedUrls, {
        onError: function (e) { return setErr(getErrorMessage(e)); },
        onSuccess: function (uploadedKeys) {
            var currentFiles = value && allowMultiple ? value.split('||') : [];
            var combinedFileUrls = __spreadArray(__spreadArray([], currentFiles, true), uploadedKeys, true);
            handleChange(combinedFileUrls.join('||'));
        }
    });
    var deleteFileMutation = useMutation(api.quote.deleteCustomerSubmissionFiles, {
        onError: function (e) {
            setErr('Failed to delete file. Please try again.');
        }
    });
    var isUploading = uploadFileMutation.isLoading;
    var isDeleting = deleteFileMutation.isLoading;
    var handleFileChange = function (e) { return __awaiter(void 0, void 0, void 0, function () {
        var files, error_1, uploadError_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setErr('');
                    if (!(e.target.files && e.target.files.length > 0)) return [3 /*break*/, 5];
                    files = Array.from(e.target.files);
                    error_1 = validateFiles(files);
                    if (error_1) {
                        setErr(error_1);
                        return [2 /*return*/];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, uploadFileMutation.mutateAsync({
                            externalID: extCustomerID,
                            files: files,
                            quoteId: activeDraftQuote
                        })];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3:
                    uploadError_1 = _a.sent();
                    console.error('Upload Files failed', uploadError_1);
                    return [3 /*break*/, 5];
                case 4:
                    if (fileInputRef.current)
                        fileInputRef.current.value = '';
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var handleClearAll = function () { return __awaiter(void 0, void 0, void 0, function () {
        var filesToDelete, deleteError_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!value)
                        return [2 /*return*/];
                    filesToDelete = value.split('||');
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, deleteFileMutation.mutateAsync({ files: filesToDelete })];
                case 2:
                    _a.sent();
                    handleChange('');
                    setConfirmDeleteAll(false);
                    setErr('');
                    return [3 /*break*/, 4];
                case 3:
                    deleteError_1 = _a.sent();
                    console.error('Delete all failed:', deleteError_1);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var handleDeleteSingleFile = function (fileUrlToDelete) { return __awaiter(void 0, void 0, void 0, function () {
        var currentFiles, updatedFiles, deleteError_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, 3, 4]);
                    setDeletingFileUrl(fileUrlToDelete);
                    return [4 /*yield*/, deleteFileMutation.mutateAsync({ files: [fileUrlToDelete] })];
                case 1:
                    _a.sent();
                    currentFiles = value ? value.split('||') : [];
                    updatedFiles = currentFiles.filter(function (f) { return f !== fileUrlToDelete; });
                    handleChange(updatedFiles.join('||'));
                    return [3 /*break*/, 4];
                case 2:
                    deleteError_2 = _a.sent();
                    console.error('Single file deletion failed:', deleteError_2);
                    return [3 /*break*/, 4];
                case 3:
                    setDeletingFileUrl(null);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        function handleClickOutside(event) {
            if (confirmDeleteAll &&
                containerRef.current &&
                !containerRef.current.contains(event.target)) {
                setConfirmDeleteAll(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function () { return document.removeEventListener('mousedown', handleClickOutside); };
    }, [confirmDeleteAll]);
    var fileList = value ? value.split('||') : [];
    var hasFiles = fileList.length > 0;
    var isError = error || !!err;
    if (variant === 'modern') {
        return (React.createElement("div", { className: "flex flex-col w-full items-center" },
            React.createElement("label", { dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(label) }, htmlFor: questionKey, className: "text-2xl font-bold text-center mb-4 ".concat(isError ? 'text-[#EA4335]' : 'text-[#1F2751]') }),
            React.createElement("div", { className: "w-full bg-[#F0F4FF] border border-dashed rounded-xl transition-colors ".concat(isError
                    ? 'border-[#EA4335] bg-red-50'
                    : 'border-[#6C70CC] hover:bg-[#E6EBF9]') }, hasFiles ? (React.createElement("div", { className: "flex flex-col gap-3 p-8" },
                fileList.map(function (fileUrl) { return (React.createElement("div", { key: fileUrl, className: "flex items-center justify-between bg-white p-3 rounded-lg shadow-sm border border-[#E9EDF7]" },
                    React.createElement("div", { className: "flex items-center gap-3 overflow-hidden" },
                        React.createElement("div", { className: "bg-[#EEF2FF] p-2 rounded-lg" },
                            React.createElement(FileIcon, null)),
                        React.createElement("span", { className: "text-sm text-[#252846] truncate", title: fileUrl }, fileUrl.split('/').pop())),
                    React.createElement("button", { type: "button", onClick: function () { return handleDeleteSingleFile(fileUrl); }, disabled: isDeleting || isUploading, className: "text-[#EA4335] hover:text-red-700 transition-colors p-2 disabled:opacity-50" }, deletingFileUrl === fileUrl ? (React.createElement("div", { className: "loader p-1 w-5 bg-red-500 animate-spin rounded-full h-5" })) : (React.createElement(FontAwesomeIcon, { icon: faTrashCan }))))); }),
                React.createElement("div", { className: "flex justify-end gap-3 mt-2" }, allowMultiple && (React.createElement("div", { className: "flex gap-3" },
                    React.createElement("label", { htmlFor: "file-upload-".concat(questionKey, "-add"), className: "inline-flex items-center gap-1.5 px-4 py-2 bg-[#6C70CC] text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-[#5A5EAF] transition-all hover:shadow-md active:scale-95 ".concat(isUploading ? 'opacity-70 pointer-events-none' : '') },
                        isUploading ? (React.createElement("div", { className: "loader p-1 w-5 bg-white animate-spin rounded-full" })) : (React.createElement(React.Fragment, null,
                            React.createElement("span", { className: "text-lg leading-none" }, "+"),
                            React.createElement("span", null, "Add more"))),
                        React.createElement("input", { id: "file-upload-".concat(questionKey, "-add"), ref: fileInputRef, type: "file", accept: ".pdf,.jpg,.jpeg,.png,xlsx,.docx", className: "hidden", onChange: handleFileChange, multiple: allowMultiple, disabled: isUploading || isDeleting })),
                    React.createElement("div", { ref: containerRef }, !confirmDeleteAll ? (React.createElement("button", { type: "button", disabled: isDeleting || isUploading, onClick: function () { return setConfirmDeleteAll(true); }, className: "bg-primary text-white hover:bg-red-700 transition-colors p-2 px-4 flex gap-4 text-sm font-medium rounded-lg disabled:opacity-50" },
                        React.createElement("span", null, "Delete All"),
                        React.createElement(FontAwesomeIcon, { icon: faTrashCan }))) : (React.createElement("button", { type: "button", onClick: handleClearAll, disabled: isDeleting || isUploading, className: "bg-[#006400] text-white p-2 px-4 flex gap-4 text-sm font-medium rounded-lg disabled:opacity-50" }, isDeleting ? (React.createElement("div", { className: "loader p-1 w-5 bg-white animate-spin rounded-full" })) : (React.createElement("span", null, "Confirm Delete?")))))))))) : (
            // State: Empty / Upload Prompt
            React.createElement("label", { htmlFor: "file-upload-".concat(questionKey), className: "flex flex-col items-center justify-center cursor-pointer h-full py-12 px-8" },
                React.createElement("input", { id: "file-upload-".concat(questionKey), ref: fileInputRef, type: "file", className: "hidden", accept: ".pdf,.jpg,.jpeg,.png,xlsx,.docx", onChange: handleFileChange, multiple: allowMultiple, disabled: isUploading }),
                isUploading ? (React.createElement("div", { className: "loader p-2 w-16 bg-[#bcc5de] animate-spin" })) : (React.createElement("div", { className: "bg-white p-4 rounded-2xl shadow-sm mb-4" },
                    React.createElement(FileUploadIcon, null))),
                React.createElement("div", { className: "text-center" },
                    React.createElement("p", { className: "text-[#252846] text-base font-medium whitespace-pre-line" }, placeholder || 'Click to upload'))))),
            err && (React.createElement("div", { className: "text-red-500 text-sm font-medium w-full text-center mt-2 min-h-[20px]" }, err))));
    }
    return (React.createElement("div", { className: "flex flex-col w-full" },
        React.createElement("label", { htmlFor: "".concat(questionKey), className: "flex items-center text-xl ".concat(isError ? 'text-[#EA4335]' : 'text-[#252846]', " font-medium w-full text-left z-[1]") }, renderQuestionLabel(question === null || question === void 0 ? void 0 : question.isHtml, label)),
        isUploading ? (React.createElement("div", { className: "mt-2 border-2 border-dashed rounded-xl p-2 flex justify-center" },
            React.createElement("div", null,
                React.createElement("div", { className: "loader p-2 w-10 animate-spin bg-gray-400 rounded-full h-10" })))) : hasFiles ? (React.createElement("div", { className: "flex items-center justify-between gap-2 mt-2 p-3 border border-[#E9EDF7] rounded-xl" },
            React.createElement("span", { className: "flex-1 text-sm truncate ".concat(error ? 'text-[#EA4335]' : 'text-[#252846]') }, fileList.length > 1
                ? "".concat(fileList.length, " files uploaded")
                : fileList[0].split('/').pop()),
            React.createElement("button", { type: "button", onClick: handleClearAll, disabled: isDeleting, className: "text-[#EA4335] text-sm font-medium focus:outline-none disabled:opacity-50" }, isDeleting ? (React.createElement("div", { className: "loader p-1 w-5 bg-primary animate-spin rounded-full" })) : (React.createElement("span", null, "Clear"))))) : (React.createElement("div", { className: "mt-2 border-2 border-dashed rounded-xl text-center cursor-pointer transition-colors ".concat(error ? 'border-[#EA4335]' : 'border-[#E9EDF7] hover:bg-gray-50') },
            React.createElement("label", { htmlFor: "file-upload-".concat(questionKey), className: "flex flex-col items-center justify-center h-full w-full cursor-pointer p-5" },
                React.createElement("input", { id: "file-upload-".concat(questionKey), ref: fileInputRef, type: "file", className: "hidden", onChange: handleFileChange, multiple: allowMultiple }),
                React.createElement("span", { className: "text-sm ".concat(error ? 'text-[#EA4335]' : 'text-[#252846]', " ") }, placeholder || 'Click to upload your file here')))),
        React.createElement("div", { className: "text-red-500 text-sm font-medium text-left mt-1 min-h-[20px]" }, err)));
};
export default FileUpload;
